import { useState, useEffect, useCallback, useRef } from 'react';

const GET_STORAGE_INFO_URL = process.env.REACT_APP_GET_STORAGE_INFO_URL;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export interface StorageInfo {
  usedBytes: number;
  usedGB: number;
  limitGB: number;
  limitBytes: number;
  planName?: string;
  maxFileSizeMB?: number; // per-file limit for the user's plan; undefined until GetStorageInfo returns it
  usagePercent: number;
  nearLimit: boolean;    // 80%+
  urgentLimit: boolean;  // 95%+
  atLimit: boolean;      // 100%
}

// Used only until the real value loads from GetStorageInfo (or if that call fails —
// "fail open" below intentionally doesn't block uploads on a storage-info error).
export const FALLBACK_MAX_FILE_SIZE_MB = 1024; // 1 GB

interface StorageCache {
  data: StorageInfo;
  fetchedAt: number;
}

// Module-level cache so it persists across re-renders
let storageCache: StorageCache | null = null;

const defaultStorageInfo: StorageInfo = {
  usedBytes: 0,
  usedGB: 0,
  limitGB: 50,
  limitBytes: 50 * 1024 * 1024 * 1024,
  usagePercent: 0,
  nearLimit: false,
  urgentLimit: false,
  atLimit: false,
};

export function useStorageInfo(userId: string | null) {
  const [storageInfo, setStorageInfo] = useState<StorageInfo>(
    storageCache?.data ?? defaultStorageInfo
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchStorageInfo = useCallback(async (force = false) => {
    if (!userId || !GET_STORAGE_INFO_URL) return;

    // Return cached data if still fresh
    if (!force && storageCache && Date.now() - storageCache.fetchedAt < CACHE_DURATION_MS) {
      setStorageInfo(storageCache.data);
      return;
    }

    // Prevent duplicate simultaneous fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${GET_STORAGE_INFO_URL}&userId=${encodeURIComponent(userId)}`
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data: StorageInfo = await response.json();

      storageCache = { data, fetchedAt: Date.now() };
      setStorageInfo(data);
    } catch (err: any) {
      console.error('useStorageInfo error:', err);
      setError(err.message);
      // Fail open — don't block the user if check fails
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [userId]);

  // Fetch on mount and when userId changes
  useEffect(() => {
    fetchStorageInfo();
  }, [fetchStorageInfo]);

  // Expose a refresh function to call after uploads or deletes
  const refresh = useCallback(() => {
    storageCache = null;
    fetchStorageInfo(true);
  }, [fetchStorageInfo]);

  return { storageInfo, loading, error, refresh };
}
